import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProjectService } from '../../../services/project.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ToastService } from '../../../services/toast.service';

interface DropdownOption { label: string; value: any; }

@Component({
  selector: 'app-project-financial-statement-reports',
  templateUrl: './project-financial-statement-reports.component.html',
  styleUrls: ['./project-financial-statement-reports.component.scss']
})
export class ProjectFinancialStatementReportsComponent implements OnInit, OnDestroy {
  reportForm!: FormGroup;
  loading = false;

  projects: any[] = [];
  projectsDropdown: DropdownOption[] = [];

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
    private _projectsService: ProjectService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadProjects();

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this._toastService.showToast('success', 'Reporte generado exitosamente. Descargando...');
        this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this._toastService.showToast('error',
          `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando estado financiero del proyecto...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  private initForm(): void {
    this.reportForm = this.fb.group({
      projectId: [null, Validators.required]
    });
  }

  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return [];
  }

  private setProjects(res: any): void {
    this.projects = this.extractData(res);
    this.projectsDropdown = this.projects.map((p: any) => ({
      label: `${p.projectId} - ${p.name}`,
      value: p.id
    }));
  }

  private loadProjects(): void {
    // getLookup: lista mínima (id+label) para dropdowns; evita el getAll() pesado
    // (grafos profundos de Budgets) — ver project.service.ts.
    this._projectsService.getLookup().subscribe({
      next: (res: any) => this.setProjects(res),
      error: () => this._toastService.showToast('error', 'No se pudieron cargar los proyectos.')
    });
  }

  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) { this.loadProjects(); return; }
    this._projectsService.getLookup(query).subscribe({
      next: (res: any) => this.setProjects(res),
      error: () => this._toastService.showToast('error', 'No se pudieron cargar los proyectos.')
    });
  }

  get selectedProjectName(): string {
    if (!this.reportForm.get('projectId')?.value) return 'Sin seleccionar';
    const project = this.projects.find(p => p.id === this.reportForm.get('projectId')?.value);
    return project ? `${project.projectId} - ${project.name}` : 'Sin seleccionar';
  }

  downloadPdf(): void {
    const projectId = this.reportForm.get('projectId')?.value;
    if (!projectId) {
      this._toastService.showToast('warning', 'Selecciona un proyecto');
      return;
    }

    this.loading = true;
    this._reportJobsService.queueProjectFinancialStatement(projectId).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this._toastService.showToast('info', 'Generando reporte en segundo plano...');
      },
      error: (error: any) => {
        console.error('Error al solicitar reporte:', error);
        this.loading = false;
        this._toastService.showToast('error', 'Hubo un error al solicitar el reporte.');
      }
    });
  }
}
